import React, {useEffect, useState, useRef, useMemo} from 'react'
import {Link as ReactRouterLink, useLocation, useHistory} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {VStack, Text, useMultiStyleConfig, StackProps, TextProps, Skeleton, SkeletonProps} from '@chakra-ui/react'
import {Button} from '_uiComponents'
import {useAnalytics} from '_customHooks'

interface PaginationProps {
    start?: number
    end?: number
    total?: number
    limit?: number
    userAddedNewFilters?: {current: boolean}
    searchTerm?: string
    isLoading?: boolean
    isRefetching?: boolean
    onLoadMore?: (showItems: number) => void
    userLoadMore?: {current: boolean}
    shownItemsNo?: number
}

/**
 * @name Pagination
 *
 * @component
 *
 * @description
 * Component representing pagination functionality.
 *
 * @param {PaginationProps} props - The properties for the pagination component.
 *
 * @returns {React.FC<PaginationProps>} The pagination component.
 */
const Pagination: React.FC<PaginationProps> = ({
    start,
    end = 0,
    total = 0,
    limit = 0,
    userAddedNewFilters,
    searchTerm,
    isLoading = false,
    isRefetching,
    onLoadMore,
    userLoadMore,
    shownItemsNo,
}) => {
    const styles = useMultiStyleConfig('Pagination') as Record<string, StackProps & TextProps>
    const location = useLocation()
    const history = useHistory()
    const prevEnd = useRef(limit)

    const {formatMessage} = useIntl()
    const {analyticsEventLoadMore} = useAnalytics()

    const [isPaginationClicked, setIsPaginationClicked] = useState(false)
    const [shouldScrollUp, setShouldScrollUp] = useState(false)

    const remainingItems = end != 0 ? total - end : total - limit
    const showItems = remainingItems < limit ? remainingItems : limit

    const nextPageUrl = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        let urlStart = start
        let urlEnd = end
        if (start == end) {
            urlStart = limit
            urlEnd = limit * 2 > total ? total : limit * 2
        } else if (end > 0 && end < total && end + limit <= total) {
            urlStart = end
            urlEnd = end + limit
        } else if (end + limit > total) {
            urlStart = end
            urlEnd = total
        }
        searchParams.set('start', String(urlStart))
        searchParams.set('sz', String(urlEnd))
        return `${location.pathname}?${searchParams.toString()}`
    }, [location.pathname, location.search, start, end, limit, total])

    const paginationMessage = !searchTerm
        ? formatMessage(
              {
                  id: 'label.pageSize',
                  defaultMessage: '1 - {0} di {1} prodotti',
              },
              {0: shownItemsNo ? shownItemsNo : end > 0 ? (end < total ? end : total) : limit < total ? limit : total, 1: total},
          )
        : formatMessage(
              {
                  id: 'tab.nav.search.totalresults',
                  defaultMessage: '{0} results for "{1}"',
              },
              {
                  0: total,
                  1: (
                      <Text as="span" {...styles.searchTerm}>
                          {searchTerm}
                      </Text>
                  ),
              },
          )

    const buttonMessage = formatMessage(
        {
            id: 'button.more',
            defaultMessage: 'VIEW {0} MORE',
        },
        {0: shownItemsNo ? (total - shownItemsNo < limit ? total - shownItemsNo : limit) : showItems},
    )

    const handlePagination = (e) => {
        e.preventDefault()

        const isScrollAtBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 100
        const currentStart = start
        const currentEnd = end
        const currentShowItems = showItems

        setIsPaginationClicked(true)
        setShouldScrollUp(isScrollAtBottom)
        if (userAddedNewFilters) userAddedNewFilters.current = true
        if (userLoadMore) userLoadMore.current = true

        requestAnimationFrame(() => {
            const url = new URL(window.location.href)
            url.searchParams.set('start', String(currentStart))
            let newEnd = currentEnd
            if (currentEnd == 0) {
                newEnd = limit * 2 > total ? total : limit * 2
            } else {
                newEnd = currentEnd + currentShowItems
            }
            url.searchParams.set('sz', String(newEnd))
            history.push(url.pathname + url.search)

            analyticsEventLoadMore()
            if (onLoadMore) {
                onLoadMore(currentShowItems)
            }
        })
    }

    const computePrevEnd = () => {
        if (end !== 0) {
            if (showItems < limit) {
                if (showItems > 0) {
                    prevEnd.current = end - limit
                } else {
                    prevEnd.current = prevEnd.current + limit > end ? prevEnd.current : prevEnd.current + limit
                }
            } else {
                prevEnd.current = end - limit
            }
        } else {
            prevEnd.current = limit
        }
    }

    useEffect(() => {
        computePrevEnd()

        if (typeof window !== 'undefined' && isPaginationClicked && !isRefetching) {
            const callback = () => {
                const newItemsLoaded = end - prevEnd.current
                const productTiles = document.querySelectorAll('.product-tile-link')
                const productTileToFocus = productTiles[end - newItemsLoaded] as HTMLElement | undefined

                if (productTileToFocus || productTiles.length > end - newItemsLoaded) {
                    if (window && !document?.querySelector('body')?.classList.contains('no-focus-outline')) {
                        productTileToFocus?.focus({preventScroll: true})
                    }

                    if (shouldScrollUp) {
                        requestAnimationFrame(() => {
                            const newScrollY = window.scrollY - 300
                            window.scrollTo({top: newScrollY, behavior: 'smooth'})
                            setShouldScrollUp(false)
                        })
                    }

                    setIsPaginationClicked(false)
                }
            }

            if ('requestIdleCallback' in window) {
                requestIdleCallback(callback, {timeout: 500})
            } else {
                setTimeout(callback, 16)
            }
        }
    }, [isRefetching, isPaginationClicked, shouldScrollUp])

    return (
        <VStack {...styles.wrapper} id="pagination">
            {isLoading ? (
                <SkeletonPagination />
            ) : (
                <>
                    <Text {...styles.recap}>{paginationMessage}</Text>
                    {end < total && showItems > 0 && (
                        <Button as={ReactRouterLink} to={nextPageUrl} variant="secondary" size="sm" width="md" onClick={handlePagination}>
                            {buttonMessage}
                        </Button>
                    )}
                </>
            )}
        </VStack>
    )
}

/**
 * @name SkeletonPagination
 *
 * @component
 *
 * @description
 * Renders lazy loaded `Skeleton`.
 *
 * @returns {React.FC} Rendered `SkeletonPagination` component.
 */
const SkeletonPagination = () => {
    const styles = useMultiStyleConfig('Pagination') as Record<string, SkeletonProps>

    return <Skeleton {...styles.skeletonRecap} />
}

export {Pagination}
